import React, { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

import { API_DOMAIN } from '../../../../constants';
import AlertCompanyDetail from '../../products/components/AlertCompanyDetail';
import { ICompany } from '../../../../interfaces/companies';
import { thousands } from '../../../../utils/utils';
import { IOrder, IOrderItem } from '../../../../interfaces/order';
import { ButtonWithLoader, ColorButton } from '../../../../components/other/Buttons';
import { useOrderTableStyles } from '../hooks/useOrderTableStyles';
import CompanyInfo from './CompanyInfo';
import { Payment } from './Payment';
import { IUser } from '../../../../interfaces/user';
import { useDownloadOrderXlsx } from '../hooks/useDownloadOrder';

interface IProps {
  orders: IOrder | null;
  isVendor: boolean | null;
  selectAction: (id: any) => void;
  selectedId: number;
  editStatus?: (id: any) => void;
  me?: IUser | null;
  isBuyer?: boolean;
}

const OrderTable: React.FC<IProps> = ({
  orders,
  isVendor,
  selectedId,
  selectAction,
  editStatus,
  me,
  isBuyer,
}) => {
  const { classes } = useOrderTableStyles();
  const intl = useIntl();
  const [isOpenCompanyDialog, setOpenCompanyDialog] = useState(false);
  const [isOpenEstimateDialog, setOpenEstimateDialog] = useState(false);
  const { download, loadingIds, downloadOrders, loadingOrderIds, downloadOrder } =
    useDownloadOrderXlsx();

  const [showPayment, setShowPayment] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<{
    type: 'buyer' | 'seller';
    order: IOrder;
  }>({} as any);

  const [com, setCompany] = useState<ICompany>();

  const translates: { [type: string]: string } = {
    paid: intl.formatMessage({ id: 'ORDER.PAYMENT.STATUS.PAID' }),
    not_paid: intl.formatMessage({ id: 'ORDER.PAYMENT.STATUS.NOT_PAID' }),
    partially_paid: intl.formatMessage({ id: 'ORDER.PAYMENT.STATUS.PARTIALLY_PAID' }),
  };

  const orderTypeList: { type: string }[] = [
    { type: 'paid' },
    { type: 'not_paid' },
    { type: 'partially_paid' },
  ];

  const openPayment = (type: 'buyer' | 'seller', order: IOrder) => {
    setPaymentInfo({ type, order });
    setShowPayment(true);
  };

  const openModalCompany = useCallback((company: ICompany) => {
    setCompany(company);
    setOpenCompanyDialog(true);
  }, []);

  const getPriceProduct = (item: IOrderItem) => {
    if (!isVendor) return item.product_price;
    return item.vendor_price || 0;
  };

  const renderItemsTable = (items: IOrderItem[]) => (
    <table className={classes.itemsTable}>
      <thead>
        <tr className={classes.tableHeaderRow}>
          <th className={classes.thImg}>Изображение</th>
          <th className={classes.thName}>Название товара</th>
          <th className={classes.thPrice}>Цена</th>
          <th className={classes.thCount}>Количество</th>
          <th className={classes.thSum}>Сумма</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <tr key={item.id} className={classes.tableRow}>
            <td className={classes.tdImg}>
              {item.product.photos && item.product.photos.length > 0 ? (
                <img
                  alt=''
                  src={`${API_DOMAIN}/${
                    item?.product?.photos?.find((el: any) => el.main)?.small ||
                    item.product.photos[0].small
                  }`}
                  className={classes.img}
                />
              ) : (
                <img alt='' src='../../../images/placeholder.png' className={classes.img} />
              )}
            </td>
            <td className={classes.tdName}>
              <Link to={`/viewproduct/${item.product.id}`} className={classes.link}>
                {item.product.name}
              </Link>
            </td>
            <td className={classes.tdPrice}>
              {thousands(String(getPriceProduct(item)))} руб.
            </td>
            <td className={classes.tdCount}>{item.count}</td>
            <td className={classes.tdSum}>
              {thousands(String((getPriceProduct(item) * item.count).toFixed(2)))} руб.
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      {orders &&
        orders.orders?.map(order => (
          <React.Fragment key={order.id}>
            {me?.is_admin && order.company && (
              <CompanyInfo
                compBrand={order.company.brand}
                compName={order.company.name}
                rightTitle='О продавце'
                onPress={() => openModalCompany(order.company)}
              />
            )}

            <div className={classes.tableWrapper}>{renderItemsTable(order.items)}</div>

            {!isBuyer && (
              <Card className={classes.cardFooter}>
                <CardContent className={classes.containerSumVendor}>
                  <div className={classes.wrapperSumVendor}>
                    <Typography className={classes.titleVendorSum}>
                      ИТОГО ПО ПРОДАВЦУ:
                    </Typography>
                    <Typography className={classes.vendorSum}>
                      {thousands(String(isVendor ? order.vendor_summ || 0 : order.summ || 0))}{' '}
                      руб.
                    </Typography>
                  </div>

                  <Typography className={classes.paymentStatus}>
                    {translates[order.payment_status]}
                  </Typography>

                  <Box display='flex' flexDirection='row' gap='16px'>
                    <ColorButton
                      className={classes.colorButtonIconView}
                      color='#217346'
                      onClick={() => downloadOrders(order.id)}
                      title='Выгрузка предложения'
                      disabled={!!loadingIds.find(id => id === order.id)}
                    >
                      {loadingOrderIds.find(id => id === order.id) ? (
                        <CircularProgress sx={{ color: '#fff' }} size='19.5px' />
                      ) : (
                        <RequestQuoteIcon htmlColor='#fff' />
                      )}
                    </ColorButton>
                    <ButtonWithLoader
                      onPress={() =>
                        openPayment(isVendor || me?.is_admin ? 'seller' : 'buyer', order)
                      }
                    >
                      Реквизиты для оплаты
                    </ButtonWithLoader>
                  </Box>
                </CardContent>
              </Card>
            )}
          </React.Fragment>
        ))}
      <>
        {me?.is_admin && orders?.items && orders?.items.length > 0 && (
          <CompanyInfo
            compBrand={orders?.company?.brand}
            compName={orders?.company?.name}
            rightTitle='О продавце'
            onPress={() => {
              const company = orders?.company;
              if (company) {
                openModalCompany(company);
              }
            }}
          />
        )}

        {orders && orders?.items && orders.items.length > 0 && (
          <div className={classes.tableWrapper}>
            <table className={classes.itemsTable}>
              <thead>
                <tr className={classes.tableHeaderRow}>
                  <th className={classes.thImg}>Изображение</th>
                  <th className={classes.thName}>Название товара</th>
                  <th className={classes.thPrice}>Цена</th>
                  <th className={classes.thCount}>Количество</th>
                  <th className={classes.thSum}>Сумма</th>
                </tr>
              </thead>
              <tbody>
                {orders.items.map(order => (
                  <tr key={order.id} className={classes.tableRow}>
                    <td className={classes.tdImg}>
                      {order.product.photos && order.product.photos.length > 0 ? (
                        <img
                          alt=''
                          src={`${API_DOMAIN}/${
                            order?.product?.photos?.find((el: any) => el.main)?.small ||
                            order.product.photos[0].small
                          }`}
                          className={classes.img}
                        />
                      ) : (
                        <img
                          alt=''
                          src='../../../images/placeholder.png'
                          className={classes.img}
                        />
                      )}
                    </td>
                    <td className={classes.tdName}>
                      <Link to={`/viewproduct/${order.product.id}`} className={classes.link}>
                        {order.product.name}
                      </Link>
                    </td>
                    <td className={classes.tdPrice}>
                      {thousands(String(getPriceProduct(order)))} руб.
                    </td>
                    <td className={classes.tdCount}>{order.count}</td>
                    <td className={classes.tdSum}>
                      {thousands(String(getPriceProduct(order) * order.count))} руб.
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isVendor && (
          <Card className={classes.card}>
            <CardContent className={classes.containerSumVendor}>
              <div className={classes.wrapperSumVendor}>
                <Typography className={classes.titleVendorSum}>ИТОГО ПО ПРОДАВЦУ:</Typography>
                <Typography className={classes.vendorSum}>
                  {thousands(String(isVendor ? orders?.vendor_summ || 0 : orders?.summ || 0))}{' '}
                  руб.
                </Typography>
              </div>

              <div>
                {selectedId !== orders?.id ? (
                  <Typography className={classes.paymentStatus}>
                    <p>{translates[orders?.payment_status || '']}</p>
                  </Typography>
                ) : (
                  <TextField
                    select
                    margin='none'
                    value={orders?.payment_status}
                    onChange={event => {
                      editStatus &&
                        editStatus({
                          id: orders.id,
                          page: 1,
                          perPage: 20,
                          data: {
                            payment_status: event.target.value,
                            items: orders.items.map(e => ({
                              count: e.count,
                              product_id: e.product.id,
                            })),
                          },
                          userId: me?.id,
                          root: me?.is_admin,
                        });
                      selectAction(-1);
                    }}
                    variant='outlined'
                    className={classes.typeList}
                    size='small'
                  >
                    {orderTypeList &&
                      orderTypeList.map(option => (
                        <MenuItem
                          hidden={option.type === 'cart'}
                          key={option.type}
                          value={option.type}
                        >
                          {translates[option.type]}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              </div>

              <Box display='flex' flexDirection='row' gap='16px'>
                {orders && (
                  <ColorButton
                    className={classes.colorButtonIconView}
                    color='#217346'
                    onClick={() => downloadOrders(orders.id)}
                    title='Выгрузка предложения'
                    disabled={!!loadingIds.find(id => id === orders.id)}
                  >
                    {loadingOrderIds.find(id => id === orders.id) ? (
                      <CircularProgress sx={{ color: '#fff' }} size='19.5px' />
                    ) : (
                      <RequestQuoteIcon htmlColor='#fff' />
                    )}
                  </ColorButton>
                )}
                <ButtonWithLoader
                  onPress={() =>
                    orders
                      ? openPayment(isVendor || me?.is_admin ? 'seller' : 'buyer', orders)
                      : {}
                  }
                >
                  Реквизиты для оплаты
                </ButtonWithLoader>
              </Box>
            </CardContent>
          </Card>
        )}
        {(isBuyer || me?.is_admin) && (
          <Card className={classes.card}>
            <CardContent className={classes.containerSumVendor}>
              <div className={classes.wrapperSumVendor}>
                <Typography className={classes.titleVendorSum}>ИТОГО:</Typography>
                <Typography className={classes.vendorSum}>
                  {thousands(String(isVendor ? orders?.vendor_summ || 0 : orders?.summ || 0))}{' '}
                  руб.
                </Typography>
              </div>

              <div>
                {selectedId !== orders?.id ? (
                  <Typography className={classes.paymentStatus}>
                    <p>{translates[orders?.payment_status || '']}</p>
                  </Typography>
                ) : (
                  <TextField
                    select
                    margin='none'
                    value={orders?.payment_status}
                    onChange={event => {
                      editStatus &&
                        editStatus({
                          id: orders.id,
                          page: 1,
                          perPage: 20,
                          data: {
                            payment_status: event.target.value,
                            items: orders.items.map(e => ({
                              count: e.count,
                              product_id: e.product.id,
                            })),
                          },
                          userId: me?.id,
                          root: me?.is_admin,
                        });
                      selectAction(-1);
                    }}
                    variant='outlined'
                    className={classes.typeList}
                    size='small'
                  >
                    {orderTypeList &&
                      orderTypeList.map(option => (
                        <MenuItem
                          hidden={option.type === 'cart'}
                          key={option.type}
                          value={option.type}
                        >
                          {translates[option.type]}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              </div>

              <Box display='flex' flexDirection='row' gap='16px'>
                {orders && (
                  <ColorButton
                    className={classes.colorButtonIconView}
                    color='#217346'
                    onClick={() => downloadOrder(orders.id)}
                    title='Выгрузка предложения'
                    disabled={!!loadingIds.find(id => id === orders.id)}
                  >
                    {loadingOrderIds.find(id => id === orders.id) ? (
                      <CircularProgress sx={{ color: '#fff' }} size='19.5px' />
                    ) : (
                      <RequestQuoteIcon htmlColor='#fff' />
                    )}
                  </ColorButton>
                )}
                <ButtonWithLoader
                  onPress={() =>
                    orders ? openPayment(isVendor ? 'seller' : 'buyer', orders) : {}
                  }
                >
                  Реквизиты для оплаты
                </ButtonWithLoader>
              </Box>
            </CardContent>
          </Card>
        )}
        <AlertCompanyDetail
          open={isOpenCompanyDialog}
          message={intl.formatMessage({ id: 'FILE.IMPORT.ALERT.TEXT' })}
          company={com}
          handleClose={() => {
            setOpenCompanyDialog(false);
          }}
        />

        <Payment
          open={showPayment}
          handleClose={() => {
            setShowPayment(false);
            setPaymentInfo({} as any);
          }}
          order={paymentInfo.order}
          type={paymentInfo.type}
        />

        <AlertCompanyDetail
          open={isOpenEstimateDialog}
          message={intl.formatMessage({ id: 'FILE.IMPORT.ALERT.TEXT' })}
          handleClose={() => {
            setOpenEstimateDialog(false);
          }}
        />
      </>
    </>
  );
};

export default OrderTable;
